import { ArchiveAdd } from "iconsax-reactjs"
import { Copy } from "lucide-react"
import { useMemo } from "react"
import { toast } from "sonner"

// Utils and Constants
// Types
import { LoadingSpinner } from "@/components/shared"
import { Button } from "@/components/ui"
import {
  ProductDescription,
  ProductGallery,
  ProductInfo
} from "@/features/jobs/affiliates/components/product"
import {
  AffiliateGuide,
  CampaignLoadingSkeleton,
  CommissionRateBanner
} from "@/features/jobs/explore/components/campaign"
import { EFolderAssets, EMediaType } from "@/modules/helpers/enum"
import {
  useAffiliateActions,
  useAffiliateDetails,
  useAffiliateMutations
} from "@/modules/helpers/hooks"
import { useLayoutStore } from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { UI_TEXT } from "@/modules/helpers/tap-constants"
import { Affiliate, AffiliateProduct } from "@/modules/types"

interface AffiliateProductDetailsProps {
  id?: string
  layoutBanner?: "upper" | "after"
  isShowAction?: boolean
  preload?: boolean
  data?: Affiliate
}

export function AffiliateProductDetails({
  id,
  layoutBanner = "after",
  isShowAction = true,
  preload = false,
  data
}: AffiliateProductDetailsProps) {
  const { insetBottom } = useLayoutStore()

  // Always call hook unconditionally, use enabled to control fetching
  const affiliateQuery = useAffiliateDetails(id, { enabled: !preload })

  // Use preloaded data if available, otherwise use query data
  const affiliate = preload ? data : affiliateQuery.data?.data?.data
  const isLoading = preload ? false : affiliateQuery.isLoading
  const refetch = preload ? undefined : affiliateQuery.refetch

  const { saveLink, save, unSave } = useAffiliateMutations(id, {
    onSaveLinkSuccess: () => {
      toast.success("Đã sao chép liên kết")
      refetch?.()
    },
    onSaveSuccess: () => {
      toast.success("Đã lưu sản phẩm")
      refetch?.()
    },
    onUnsaveSuccess: () => {
      toast.success("Đã bỏ lưu sản phẩm")
      refetch?.()
    }
  })

  // Memoized affiliate details
  const details = useMemo((): AffiliateProduct | undefined => {
    return affiliate as AffiliateProduct
  }, [affiliate])

  // Action handlers
  const { handleCopyLink, handleAttachAffiliate } = useAffiliateActions(
    details?.id,
    details?.link,
    (id) => saveLink.mutate(id)
  )

  const images = details?.images?.map((url: string) => ({ type: EMediaType.IMAGE, url })) || []

  if (isLoading && !preload) {
    return (
      <div className="flex justify-center items-center h-full ">
        <LoadingSpinner className="w-20 h-20" />
      </div>
    )
  }

  return (
    <>
      {isLoading && <CampaignLoadingSkeleton />}
      {!isLoading && (
        <div className="flex flex-col overflow-y-auto pb-30 px-3">
          {layoutBanner === "upper" && (
            <p className="text-xs font-semibold mb-3">{UI_TEXT.PRODUCT_DETAILS}</p>
          )}
          <div className="gap-4 h-fit flex flex-col">
            <div>
              <ProductGallery
                prefix={EFolderAssets.INSPIRATION_AFFILIATE_CAMPAIGN}
                withFullURL
                ownerId={details?.id}
                data={images}
              />
            </div>
            <div className="flex flex-col gap-2 flex-1">
              {layoutBanner === "after" && (
                <CommissionRateBanner
                  commissionRate={details?.creatorCommissionRate}
                  maxCommission={details?.creatorCommissionValue}
                />
              )}
              <ProductInfo
                title={details?.title}
                productId={details?.productId}
                price={details?.price}
                productDetailReview={details?.productDetailReview as any}
              />
              <AffiliateGuide />

              <div className="p-1 flex flex-col gap-2 rounded-xl shadow-xs">
                <ProductDescription data={details?.descDetail} />
              </div>
            </div>
          </div>
          {/* <div className="space-y-2 px-2 mt-2">
              <p className="font-semibold text-xs lg:text-base">{UI_TEXT.SIMILAR_PRODUCTS}</p>
              <SimilarProducts category={details?.category} productId={details?.id} />
            </div> */}
        </div>
      )}

      {isShowAction && (
        <div
          className="p-2 flex w-full fixed bottom-0 bg-white gap-2 right-0 left-0"
          style={{ bottom: insetBottom }}
        >
          <Button
            variant={"ghost"}
            onClick={() => {
              if (!details?.id) return
              if (details?.isSaved) {
                unSave.mutate(details.id)
              } else {
                save.mutate(details.id)
              }
            }}
            className={cn("w-12 rounded-xl !h-12 border border-primary bg-gray-100", {
              "border-primary-800 bg-background": details?.isSaved
            })}
          >
            <ArchiveAdd
              className={cn("text-primary", {
                "text-primary-800": details?.isSaved
              })}
              size={10}
              variant={details?.isSaved ? "Bold" : "Linear"}
            />
          </Button>
          <Button
            variant={"ghost"}
            onClick={handleCopyLink}
            className="w-12 rounded-xl !h-12 border border-primary bg-gray-100"
          >
            <Copy className="text-primary" size={10} />
          </Button>
          <Button
            onClick={handleAttachAffiliate}
            className="flex-1 text-xs rounded-xl !h-12 bg-primary-800 border-primary-100 border"
          >
            Thêm vào trưng bày
          </Button>
        </div>
      )}
    </>
  )
}
